// src/pages/Previewedit.jsx
import React, { useState, useRef, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import '../styles/Preview.css';
import { FaBell, FaUserCircle } from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

// ----- all question types -----
const questionTypes = [
  { label: "M.C.Q", value: "mcq" },
  { label: "True-False", value: "true_false" },
  { label: "Fill in the blanks", value: "fill_in_the_blanks" },
  { label: "Very Short Question Answer", value: "very_short_qa" },
  { label: "Short Question Answer", value: "short_qa" },
  { label: "Long Question Answer", value: "long_qa" },
  { label: "Diagram-Based/Image-Based Questions", value: "diagram_image_based" },
  { label: "Matching Types", value: "matching_type" },
];

const Previewedit = () => {
  const { id: paperId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // ----- Paper info -----
  const [paperName, setPaperName] = useState('');
  const [date, setDate] = useState('');
  const [duration, setDuration] = useState('');
  const [totalMarks, setTotalMarks] = useState('');
  const [typeOfPaper, setTypeOfPaper] = useState('');
  const [marksPerType, setMarksPerType] = useState({});

  // ----- Dropdown & modal -----
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // ----- Dashboard & user -----
  const [user, setUser] = useState({ id: null, name: '', email: '', password: '' });
  const [activeItem, setActiveItem] = useState('papers');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // ----- Board / Grade / Subject -----
  const [boardId, setBoardId] = useState(null);
  const [gradeId, setGradeId] = useState(null);
  const [subjectId, setSubjectId] = useState(null);

  // ----- Selected questions -----
  const [selectedQuestions, setSelectedQuestions] = useState({});
  const [questionBank, setQuestionBank] = useState({});
  const [allowedCounts, setAllowedCounts] = useState({});

  // ----- Fetch paper info -----
  useEffect(() => {
    const fetchPaper = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`https://digital.tifleducation.com/admin/public/api/papers/${paperId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const paper = res.data.data.paper;

        // always set from DB
        setPaperName(paper.paper_name || '');
        setDate(paper.date ? paper.date.split("T")[0] : '');
        setDuration(paper.duration || '');
        setTotalMarks(paper.total_marks || '');
        setTypeOfPaper(paper.type_of_paper || '');
        setBoardId(paper.board);
        setGradeId(paper.grade);
        setSubjectId(paper.subject);

        const formattedMarks = {};
        Object.entries(paper.marks || {}).forEach(([key, value]) => {
          formattedMarks[key] = value?.toString() || '';
        });
        setMarksPerType(formattedMarks);

        // 🔥 Use updated questions from Regularpaperedit if present
        if (location.state?.selected_questions) {
          setSelectedQuestions(location.state.selected_questions);
        } else {
          setSelectedQuestions(paper.chapter_wise_options || {});
        }

        // ✅ Fetch allowedCounts from DB (same as Regularpaperedit)
        setAllowedCounts({
          mcq: Number(paper.mcq || 0),
          fill_in_the_blanks: Number(paper.fill_in_the_blanks || 0),
          true_false: Number(paper.true_false || 0),
          very_short_qa: Number(paper.very_short_qa || 0),
          short_qa: Number(paper.short_qa || 0),
          long_qa: Number(paper.long_qa || 0),
          matching_type: Number(paper.matching_type || 0),
          diagram_image_based: Number(paper.diagram_image_based || 0),
        });

      } catch (error) {
        console.error("Error fetching paper data:", error.response?.data || error);
        toast.error("Failed to load paper data");
      }
    };

    if (paperId) fetchPaper();
  }, [paperId, location.state]);

  // ----- Fetch user -----
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get("https://digital.tifleducation.com/admin/public/api/getDashboard", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data?.user) {
          setUser({
            id: res.data.user.id,
            name: res.data.user.name || '',
            email: res.data.user.email || '',
            password: '',
          });
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error.response?.data || error);
      }
    };
    fetchUser();
  }, []);

  // ----- Fetch question details -----
  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const token = localStorage.getItem("token");
        const allIds = [];

        Object.values(selectedQuestions).forEach(chapter => {
          Object.values(chapter).forEach(arr => allIds.push(...arr));
        });

        const uniqueIds = [...new Set(allIds)];
        if (!uniqueIds.length) return;

        const res = await axios.post(
          "https://digital.tifleducation.com/admin/public/api/papers/get-questions-by-ids",
          { ids: uniqueIds },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const qMap = {};
        res.data.data.forEach(q => (qMap[q.id] = q));
        setQuestionBank(qMap);
      } catch (error) {
        console.error("Error fetching question details:", error);
      }
    };

    if (Object.keys(selectedQuestions).length > 0) fetchQuestions();
  }, [selectedQuestions]);

  // ----- Profile save -----
  const handleSaveProfile = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        "https://digital.tifleducation.com/admin/public/api/update-profile",
        { name: user.name, password: user.password },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Profile updated successfully!");
      setIsModalOpen(false);
      setUser({ ...user, password: '' });
    } catch (error) {
      console.error("Error updating profile:", error.response?.data || error);
      toast.error("Failed to update profile");
    }
  };

  // ----- Dropdown -----
  const toggleDropdown = () => setShowDropdown(prev => !prev);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("click", handleClickOutside, true);
    return () => document.removeEventListener("click", handleClickOutside, true);
  }, []);

  // ----- Helpers -----
  const getQuestionsByType = (typeKey) => {
    const allQuestions = [];
    Object.values(selectedQuestions).forEach(chapter => {
      if (chapter[typeKey]) allQuestions.push(...chapter[typeKey]);
    });
    return allQuestions;
  };

  const getCounts = () => {
    const counts = {};
    questionTypes.forEach(type => {
      const count = getQuestionsByType(type.value).length;
      if (count > 0) counts[type.value] = count;
    });
    return counts;
  };

  // ----- Export Paper -----
  const handleExportPaper = async () => {
    try {
      const token = localStorage.getItem("token");

      const res = await axios.get(`https://digital.tifleducation.com/admin/public/api/papers/export/${paperId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "json",
      });

      if (res.data.status === "success" && res.data.file_url) {
        window.open(res.data.file_url, "_blank"); // Open PDF in new tab
        toast.success("Paper PDF opened in new tab!");

        // Clear local storage/session if needed
        localStorage.removeItem("selected_questions");
        localStorage.removeItem("board_id");
        localStorage.removeItem("grade_id");
        localStorage.removeItem("subject_id");

        // Redirect current page
        setTimeout(() => {
          navigate("/papers/mypapers");
        }, 500); // small delay to ensure PDF tab opens
      } else {
        toast.error("Failed to generate PDF");
      }
    } catch (error) {
      console.error("Error exporting paper:", error.response?.data || error);
      toast.error("Failed to export paper");
    }
  };

  // ----- Export Answer Key -----
  const handleExportAnswerKey = async () => {
    try {
      const token = localStorage.getItem("token");

      const res = await axios.get(`https://digital.tifleducation.com/admin/public/api/papers/export-answer-key/${paperId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "json",
      });

      if (res.data.status === "success" && res.data.file_url) {
        window.open(res.data.file_url, "_blank"); // Open PDF in new tab
        toast.success("Answer Key PDF opened in new tab!");

        // Clear local storage/session if needed
        localStorage.removeItem("selected_questions");
        localStorage.removeItem("board_id");
        localStorage.removeItem("grade_id");
        localStorage.removeItem("subject_id");

        // Redirect current page
        setTimeout(() => {
          navigate("/papers");
        }, 500);
      } else {
        toast.error("Failed to generate answer key PDF");
      }
    } catch (error) {
      console.error("Error exporting answer key:", error.response?.data || error);
      toast.error("Failed to export answer key");
    }
  };


  // ----- Save paper -----
  const handleSavePaper = async (action = "save") => {
    try {
      const token = localStorage.getItem("token");
      const bId = Number(boardId || localStorage.getItem("board_id"));
      const gId = Number(gradeId || localStorage.getItem("grade_id"));
      const sId = Number(subjectId || localStorage.getItem("subject_id"));

      if (!paperName || !date || !duration || !totalMarks || !bId || !gId || !sId) {
        toast.error("Please fill all required fields");
        return;
      }
      if (!typeOfPaper || !["Assessment", "Worksheet"].includes(typeOfPaper)) {
        toast.error("Please select a valid Type of Paper");
        return;
      }

      // filter out empty questions
      const filteredSelectedQuestions = {};
      Object.entries(selectedQuestions).forEach(([chapterId, types]) => {
        const cleanTypes = {};
        Object.entries(types).forEach(([type, qIds]) => {
          if (qIds && qIds.length > 0) cleanTypes[type] = qIds;
        });
        if (Object.keys(cleanTypes).length > 0) {
          filteredSelectedQuestions[chapterId] = cleanTypes;
        }
      });

      if (Object.keys(filteredSelectedQuestions).length === 0) {
        toast.error("Please select at least one question");
        return;
      }

      // only include marks for non-empty
      const filteredMarks = {};
      questionTypes.forEach(type => {
        if (getQuestionsByType(type.value).length > 0 && marksPerType[type.value]) {
          filteredMarks[type.value] = marksPerType[type.value];
        }
      });

      const counts = getCounts();


      const payload = {
        paper_name: paperName,
        date,
        duration,
        total_marks: Number(totalMarks),
        board: bId,
        grade: gId,
        subject: sId,
        selected_questions: filteredSelectedQuestions,
        action,
        paper_type: "regular",
        type_of_paper: typeOfPaper,
        marks: filteredMarks,
        mcq: counts.mcq ?? 0,
        fill_in_the_blanks: counts.fill_in_the_blanks ?? 0,
        true_false: counts.true_false ?? 0,
        very_short_qa: counts.very_short_qa ?? 0,
        short_qa: counts.short_qa ?? 0,
        long_qa: counts.long_qa ?? 0,
        matching_type: counts.matching_type ?? 0,
        diagram_image_based: counts.diagram_image_based ?? 0,
      };
      // console.log("=== Payload to Save Paper ===");
      // console.log(payload);
      // console.log("=============================");
      if (paperId) {
        await axios.put(`https://digital.tifleducation.com/admin/public/api/papers/${paperId}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Paper updated successfully!");
      } else {
        await axios.post("https://digital.tifleducation.com/admin/public/api/papers", payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Paper created successfully!");
      }

      if (action === "save") navigate("/papers/draftpaper/");
      else navigate("/papers");

    } catch (error) {
      console.error("Error saving paper:", error.response?.data || error);
      toast.error(error.response?.data?.message || "Failed to save paper");
    }
  };

  return (
    <div className="dashboard-container">
      <Sidebar
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
        activeItem={activeItem}
        setActiveItem={setActiveItem}
      />

      <main className="main-content">
        {/* Header */}
        <div className="preview-header">
          <div className="preview-welcome-text">
            <button onClick={() => navigate(-1)} className="back-link-prev">
              <i className="fa fa-arrow-left" aria-hidden="true"></i>
            </button>
            <span className="preview-title">Preview Paper</span>
          </div>
          <div className="header-icons" ref={dropdownRef}>
            <FaBell className="icon" />
            <div className="user-dropdown-wrapper">
              <FaUserCircle className="icon" onClick={toggleDropdown} />
              {showDropdown && (
                <div className="user-dropdown">
                  <div className='edituser' onClick={() => setIsModalOpen(true)}>
                    <FaUserCircle className="ditusericon" />
                    <span>Edit Profile</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Paper Info */}
        <div className="dropdown-row">
          <div className="dropdown-group">
            <label>Paper Name <span style={{ color: 'red' }}>*</span></label>
            <input type="text" className="input-field" value={paperName} onChange={e => setPaperName(e.target.value)} />
          </div>
          <div className="dropdown-group">
            <label>Type of Paper <span style={{ color: 'red' }}>*</span></label>
            <select value={typeOfPaper} onChange={e => setTypeOfPaper(e.target.value)}>
              <option value="">Select Type of Paper</option>
              <option value="Worksheet">Worksheet</option>
              <option value="Assessment">Assessment</option>
            </select>
          </div>
          <div className="dropdown-group">
            <label>Date <span style={{ color: 'red' }}>*</span></label>
            <input type="date" className="input-field" value={date} onChange={e => setDate(e.target.value)} />
          </div>
          <div className="dropdown-group">
            <label>Duration <span style={{ color: 'red' }}>*</span></label>
            <input type="text" className="input-field" value={duration} onChange={e => setDuration(e.target.value)} />
          </div>
          <div className="dropdown-group">
            <label>Total Marks <span style={{ color: 'red' }}>*</span></label>
            <input type="number" className="input-field" value={totalMarks} onChange={e => setTotalMarks(e.target.value)} />
          </div>
        </div>

        {/* Questions */}
        <div className="preview-section">
          {questionTypes.map(type => {
            const qIds = getQuestionsByType(type.value);
            if (qIds.length === 0) return null;

            return (
              <div key={type.value}>
                <h3 className="preview-section-title">
                  • {type.label}
                  <input
                    type="text"
                    className="marks-input"
                    placeholder="Marks"
                    value={marksPerType[type.value] || ''}
                    onChange={e => setMarksPerType(prev => ({ ...prev, [type.value]: e.target.value }))}
                  />
                </h3>
                <div className="preview-box">
                  {qIds.map((qId, idx) => {
                    const q = questionBank[qId];
                    if (!q) return <div key={idx}>Loading question...</div>;

                    let imageUrl = null;
                    let questionText = q.question_title || q.question_text || "No question text";
                    if (q.question_title) {
                      const imgMatch = q.question_title.match(/<img\s+[^>]*src=["']([^"']+)["']/i);
                      if (imgMatch) {
                        imageUrl = imgMatch[1];
                        questionText = questionText.replace(/<img\s+[^>]*>/i, '');
                      }
                    }

                    return (
                      <div key={idx} className="preview-question">
                        <div className="question-header">
                          <input type="checkbox" checked readOnly />
                          <span>{idx + 1}. </span>
                          <span dangerouslySetInnerHTML={{ __html: questionText }} />
                        </div>

                        {type.value === "mcq" && q.options && (
                          <div className="preview-options">
                            {q.options.map((opt, i) => (
                              <div key={i} className="option-item">
                                <strong>{String.fromCharCode(65 + i)}.</strong> {opt.replace(/<\/?p>/g, "").trim()}
                              </div>
                            ))}
                          </div>
                        )}

                        {type.value === "diagram_image_based" && imageUrl && (
                          <div className="preview-image">
                            <img src={imageUrl} alt="Diagram" style={{ height: '150px', objectFit: 'contain' }} />
                          </div>
                        )}

                        {type.value === "matching_type" && q.left_column && q.right_column && (
                          <div className="preview-matching" style={{ display: 'flex', gap: '2rem' }}>
                            <div className="matching-column" style={{ flex: 1 }} dangerouslySetInnerHTML={{ __html: q.left_column }} />
                            <div className="matching-column" style={{ flex: 1 }} dangerouslySetInnerHTML={{ __html: q.right_column }} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Buttons */}
          <div className="preview-buttons">
            <button
              className="edit-btn"
              onClick={() => {
                if (!paperId) {
                  toast.error("Paper ID missing, cannot edit!");
                  return;
                }
                navigate(`/regularpaperedit/${paperId}`, {
                  state: { boardId, gradeId, subjectId, marks: marksPerType, selected_questions: selectedQuestions, paperName, date, duration, totalMarks, typeOfPaper, paperId }
                });
              }}
            >
              Edit
            </button>
            <button className="save-prev-btn" onClick={() => handleSavePaper("save")}>Save</button>
            <button className="export-btn" onClick={handleExportPaper}>Export Paper</button>
            <button className="export-btn" onClick={handleExportAnswerKey}>Export Answer Key</button>
          </div>
        </div>
      </main>

      {/* Profile Modal */}
      {isModalOpen && (
        <div style={modalStyles.overlay}>
          <div style={modalStyles.modal}>
            <button style={modalStyles.closeBtn} onClick={() => setIsModalOpen(false)}>&times;</button>
            <div style={modalStyles.userIcon}><FaUserCircle /></div>
            <div style={modalStyles.formGroup}>
              <label>Username</label>
              <input type="text" value={user.name} onChange={e => setUser({ ...user, name: e.target.value })} style={modalStyles.input} />
            </div>
            <div style={modalStyles.formGroup}>
              <label>Email</label>
              <input type="email" value={user.email} disabled style={{ ...modalStyles.input, background: '#f0f0f0', color: '#888' }} />
            </div>
            <div style={modalStyles.formGroup}>
              <label>Password</label>
              <input type="password" value={user.password} onChange={e => setUser({ ...user, password: e.target.value })} style={modalStyles.input} />
            </div>
            <button onClick={handleSaveProfile} style={modalStyles.saveBtn}>Save</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Previewedit;

// Modal styles
const modalStyles = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)', display: 'flex',
    justifyContent: 'center', alignItems: 'center', zIndex: 1000,
  },
  modal: {
    backgroundColor: 'white', borderRadius: '15px', padding: '30px',
    width: '400px', position: 'relative', boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
    textAlign: 'center',
  },
  closeBtn: {
    position: 'absolute', top: '10px', right: '15px',
    border: 'none', background: 'transparent',
    fontSize: '1.5rem', cursor: 'pointer', color: '#999',
  },
  userIcon: { fontSize: '60px', color: '#444', marginBottom: '15px' },
  formGroup: { marginBottom: '20px', textAlign: 'left' },
  input: { width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ccc', fontSize: '1rem' },
  saveBtn: { width: '100%', padding: '12px', borderRadius: '8px', border: 'none', background: '#007bff', color: 'white', fontSize: '1rem', cursor: 'pointer' },
};
